import {
  boundsFromPoints,
  clampBounds,
  defaultBoundsForTool,
  denormalizePenLocalPoints,
  getArrowRenderPoints,
  normalizeBounds,
  normalizePenLocalPoints,
} from './feedbackGeometry.js';
import {
  colorValue,
  DEFAULT_FEEDBACK_COLOR,
  normalizeTextBackgroundMode,
} from './feedbackTypes.js';

const ROTATABLE_TYPES = new Set(['arrow', 'rectangle', 'highlight', 'text']);
const DEFAULT_TEXT_COLOR = '#0f172a';

function round3(value) {
  return Math.round(Number(value) * 1000) / 1000;
}

function clone(value) {
  return value == null ? value : JSON.parse(JSON.stringify(value));
}

export function annotationDisplayNumber(annotationId) {
  const match = String(annotationId || '').match(/(\d+)$/);
  return match ? Number(match[1]) : Number.POSITIVE_INFINITY;
}

function compareByDisplayNumber(left, right) {
  return annotationDisplayNumber(left?.id) - annotationDisplayNumber(right?.id);
}

export function getDisplayOrderedAnnotations(annotations = []) {
  return [...annotations]
    .map((annotation, index) => ({ annotation, index }))
    .sort((left, right) => {
      const difference = compareByDisplayNumber(left.annotation, right.annotation);
      if (difference !== 0) return difference;
      return left.index - right.index;
    })
    .map((item) => item.annotation);
}

export function annotationSummaryText(annotation) {
  return (
    String(annotation?.instruction || '').trim() ||
    String(annotation?.feedbackDraft?.description || '').trim() ||
    String(annotation?.feedbackDraft?.title || '').trim() ||
    'No feedback text yet.'
  );
}

function pointObject(point) {
  if (Array.isArray(point)) return { x: Number(point[0]) || 0, y: Number(point[1]) || 0 };
  return {
    x: Number(point?.x) || 0,
    y: Number(point?.y) || 0,
  };
}

function fillColorForStroke(strokeColor) {
  return strokeColor || colorValue(DEFAULT_FEEDBACK_COLOR);
}

export function annotationSupportsRotation(type) {
  return ROTATABLE_TYPES.has(type);
}

export function annotationSupportsTextEdit(type) {
  return type === 'text';
}

export function createAnnotationStyle(type, options = {}) {
  const strokeColor = options.strokeColor || colorValue(DEFAULT_FEEDBACK_COLOR);
  if (type === 'highlight') {
    return {
      strokeColor,
      fillColor: options.fillColor || fillColorForStroke(strokeColor),
    };
  }
  if (type === 'text') {
    const boxColor = options.boxColor || strokeColor;
    return {
      strokeColor: boxColor,
      boxColor,
      textColor: options.textColor || DEFAULT_TEXT_COLOR,
      textBackgroundMode: normalizeTextBackgroundMode(options.textBackgroundMode),
    };
  }
  return {
    strokeColor,
  };
}

function boundsFromPointList(points) {
  if (!points?.length) return null;
  const mapped = points.map(pointObject);
  const xs = mapped.map((point) => point.x);
  const ys = mapped.map((point) => point.y);
  return {
    x: Math.min(...xs),
    y: Math.min(...ys),
    width: Math.max(1, Math.max(...xs) - Math.min(...xs)),
    height: Math.max(1, Math.max(...ys) - Math.min(...ys)),
  };
}

function normalizeRotation(type, rotationDeg) {
  if (!annotationSupportsRotation(type)) return 0;
  return round3(Number(rotationDeg) || 0);
}

function normalizeStyle(annotation, type) {
  const legacyStroke = colorValue(annotation?.color || DEFAULT_FEEDBACK_COLOR);
  const rawStyle = annotation?.style || {};
  if (type === 'text') {
    const boxColor = rawStyle.boxColor || rawStyle.strokeColor || legacyStroke;
    return createAnnotationStyle('text', {
      boxColor,
      strokeColor: boxColor,
      textColor: rawStyle.textColor || DEFAULT_TEXT_COLOR,
      textBackgroundMode: rawStyle.textBackgroundMode,
    });
  }
  if (type === 'highlight') {
    const strokeColor = rawStyle.strokeColor || legacyStroke;
    return createAnnotationStyle('highlight', {
      strokeColor,
      fillColor: rawStyle.fillColor || strokeColor,
    });
  }
  return createAnnotationStyle(type, {
    strokeColor: rawStyle.strokeColor || legacyStroke,
  });
}

function normalizePenLocalPoint(localPoint) {
  if (Array.isArray(localPoint)) {
    return { x: round3(localPoint[0]), y: round3(localPoint[1]) };
  }
  return { x: round3(localPoint?.x), y: round3(localPoint?.y) };
}

export function normalizeAnnotation(annotation, index = 0, image = { width: 1, height: 1 }) {
  const type = annotation?.type || 'rectangle';
  const derivedBounds =
    annotation?.bounds ||
    boundsFromPointList(
      type === 'pen' ? annotation?.points : type === 'arrow' ? annotation?.points : null,
    ) ||
    defaultBoundsForTool(type, { x: 16, y: 16 });
  const bounds = clampBounds(derivedBounds, image);
  const normalized = {
    ...clone(annotation),
    type,
    bounds,
    normalizedBounds: normalizeBounds(bounds, image),
    rotationDeg: normalizeRotation(type, annotation?.rotationDeg),
    zIndex: Number.isFinite(Number(annotation?.zIndex)) ? Number(annotation.zIndex) : index,
    style: normalizeStyle(annotation, type),
  };

  if (type === 'text') {
    const text = annotation?.text ?? annotation?.instruction ?? '';
    normalized.text = text;
    normalized.instruction = annotation?.instruction ?? text;
  } else {
    normalized.text = annotation?.text ?? '';
    normalized.instruction = annotation?.instruction || '';
  }

  if (type === 'pen') {
    normalized.localPoints = Array.isArray(annotation?.localPoints)
      ? annotation.localPoints.map(normalizePenLocalPoint)
      : normalizePenLocalPoints(annotation?.points || [], bounds);
  }

  if (type === 'arrow') {
    if (Array.isArray(annotation?.localPoints) && annotation.localPoints.length >= 2) {
      normalized.localPoints = annotation.localPoints.slice(0, 2).map(normalizePenLocalPoint);
    } else if (Array.isArray(annotation?.points) && annotation.points.length >= 2) {
      normalized.localPoints = normalizePenLocalPoints(annotation.points.slice(0, 2), bounds);
    }
  }

  return normalized;
}

export function getOrderedAnnotations(annotations = []) {
  return [...annotations].sort((left, right) => (left.zIndex ?? 0) - (right.zIndex ?? 0));
}

export function reindexAnnotations(annotations = []) {
  return annotations.map((annotation, index) => ({
    ...annotation,
    zIndex: index,
  }));
}

function packageWithCounts(pkg, screens) {
  const annotationCount = screens.reduce(
    (sum, screen) => sum + (screen.metadata?.annotations?.length || 0),
    0,
  );
  return {
    ...pkg,
    screens,
    screenCount: screens.length,
    annotationCount,
  };
}

export function screenIdForStage(stage) {
  return stage?.screenId || stage?.metadata?.screenId || stage?.id || null;
}

export function normalizeFeedbackStage(stage) {
  if (!stage) return stage;
  const image = stage.metadata?.image || { width: 1, height: 1 };
  const annotations = reindexAnnotations(
    getOrderedAnnotations(
      (stage.metadata?.annotations || []).map((annotation, index) =>
        normalizeAnnotation(annotation, index, image),
      ),
    ),
  );
  return {
    ...clone(stage),
    metadata: {
      ...clone(stage.metadata),
      annotations,
    },
  };
}

export function normalizeFeedbackPackage(pkg) {
  if (!pkg) return pkg;
  const screens = (pkg.screens || []).map((screen) => {
    const image = screen.metadata?.image || { width: 1, height: 1 };
    const annotations = reindexAnnotations(
      getOrderedAnnotations(
        (screen.metadata?.annotations || []).map((annotation, index) =>
          normalizeAnnotation(annotation, index, image),
        ),
      ),
    );
    return {
      ...clone(screen),
      metadata: {
        ...clone(screen.metadata),
        annotations,
      },
    };
  });
  return packageWithCounts(pkg, screens);
}

function nextAnnotationNumber(annotations = []) {
  return (
    annotations.reduce((max, annotation) => {
      const match = String(annotation.id || '').match(/(\d+)$/);
      return match ? Math.max(max, Number(match[1])) : max;
    }, 0) + 1
  );
}

export function nextAnnotationId(annotations = []) {
  return `A-${String(nextAnnotationNumber(annotations)).padStart(3, '0')}`;
}

function nextScreenNumber(screens = []) {
  return (
    screens.reduce((max, screen) => {
      const match = String(screenIdForStage(screen) || '').match(/(\d+)$/);
      return match ? Math.max(max, Number(match[1])) : max;
    }, 0) + 1
  );
}

export function nextScreenId(screens = []) {
  return `S-${String(nextScreenNumber(screens)).padStart(3, '0')}`;
}

export function createPointAnnotation({ id, tool, point, image, strokeColor, textBackgroundMode }) {
  const bounds = clampBounds(defaultBoundsForTool(tool, point), image);
  return normalizeAnnotation(
    {
      id,
      type: tool,
      bounds,
      text: tool === 'text' ? '' : '',
      instruction: '',
      style: createAnnotationStyle(tool, { strokeColor, textBackgroundMode }),
    },
    0,
    image,
  );
}

export function createDrawAnnotation({
  id,
  tool,
  start,
  end,
  image,
  strokeColor,
  textBackgroundMode,
}) {
  const bounds = clampBounds(boundsFromPoints(start, end), image);
  return normalizeAnnotation(
    {
      id,
      type: tool,
      bounds,
      localPoints:
        tool === 'arrow' ? normalizePenLocalPoints([start, end], bounds).slice(0, 2) : undefined,
      instruction: '',
      style: createAnnotationStyle(tool, { strokeColor, textBackgroundMode }),
    },
    0,
    image,
  );
}

export function createPenAnnotation({ id, points, image, strokeColor }) {
  const bounds = clampBounds(boundsFromPointList(points), image);
  return normalizeAnnotation(
    {
      id,
      type: 'pen',
      bounds,
      instruction: '',
      style: createAnnotationStyle('pen', { strokeColor }),
      localPoints: normalizePenLocalPoints(points, bounds),
    },
    0,
    image,
  );
}

function offsetAnnotation(annotation, dx, dy) {
  return {
    ...annotation,
    bounds: {
      ...annotation.bounds,
      x: annotation.bounds.x + dx,
      y: annotation.bounds.y + dy,
    },
  };
}

export function duplicateAnnotation(annotations = [], id) {
  const ordered = getOrderedAnnotations(annotations);
  const index = ordered.findIndex((annotation) => annotation.id === id);
  if (index === -1) return ordered;
  const duplicate = clone(ordered[index]);
  duplicate.id = nextAnnotationId(ordered);
  duplicate.zIndex = ordered[index].zIndex + 1;
  const duplicated = offsetAnnotation(duplicate, 24, 24);
  const next = [...ordered];
  next.splice(index + 1, 0, duplicated);
  return reindexAnnotations(next);
}

export function bringAnnotationForward(annotations = [], id) {
  const ordered = getOrderedAnnotations(annotations);
  const index = ordered.findIndex((annotation) => annotation.id === id);
  if (index === -1 || index === ordered.length - 1) return reindexAnnotations(ordered);
  const next = [...ordered];
  [next[index], next[index + 1]] = [next[index + 1], next[index]];
  return reindexAnnotations(next);
}

export function sendAnnotationBackward(annotations = [], id) {
  const ordered = getOrderedAnnotations(annotations);
  const index = ordered.findIndex((annotation) => annotation.id === id);
  if (index <= 0) return reindexAnnotations(ordered);
  const next = [...ordered];
  [next[index - 1], next[index]] = [next[index], next[index - 1]];
  return reindexAnnotations(next);
}

export function annotationText(annotation) {
  if (annotation?.type === 'text') return annotation?.text || annotation?.instruction || '';
  return annotation?.instruction || '';
}

export function materializePenPoints(annotation) {
  return denormalizePenLocalPoints(annotation.localPoints || [], annotation.bounds);
}

export function materializeArrowPoints(annotation) {
  return getArrowRenderPoints(annotation.bounds, annotation.localPoints);
}
